'use client';

import { CTAButton } from './CTAButton';
import { trackLandingCtaClicked } from '@/lib/analytics/posthog';

type Variant = 'primary' | 'secondary' | 'ghost' | 'inverted';
type Size = 'md' | 'lg';

interface SignupCTAProps {
  children: React.ReactNode;
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  ariaLabel?: string;
  className?: string;
  /** Tracking source — appended to the /register URL as ?source=… so
   *  abandoned-recovery + analytics can attribute drop-offs to specific
   *  CTAs. Defaults to "cta" for the generic catch-all. */
  source?: string;
}

/**
 * SignupCTA — F-500 2026 consolidation: navigates directly to the
 * `/register` page instead of opening a SignupModal first. The old
 * modal step was a doubled mental-load (modal asks for email →
 * /register asks for 4 more fields) — Linear/Stripe/Vercel all collapse
 * that into a single dedicated page. Renders an `<a href>` rather than
 * an onClick so it stays SSR-friendly, opens in same tab + works without
 * JS, and search bots can crawl it. The onClick below is pure progressive
 * enhancement — it fires the `landing_cta_clicked` PostHog event but the href
 * navigation proceeds regardless (works with JS disabled / for crawlers).
 */
export function SignupCTA(props: SignupCTAProps) {
  const source = props.source ?? 'cta';
  return (
    <CTAButton
      variant={props.variant ?? 'primary'}
      size={props.size ?? 'lg'}
      fullWidth={props.fullWidth}
      ariaLabel={props.ariaLabel}
      className={props.className}
      href={`/register?source=${encodeURIComponent(source)}`}
      onClick={() => trackLandingCtaClicked(source)}
    >
      {props.children}
    </CTAButton>
  );
}
